/**
 * Preview de costo estimado para un placement (sin persistir).
 * Llama /media/outlets/{id}/estimate-price cuando outlet + params cambian.
 */
import { useQuery } from "@tanstack/react-query";
import { Calculator } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { media } from "@/lib/api";

export interface PriceEstimatePreviewProps {
  outletId: number | null;
  wordCount: number | null;
  spotDurationSeconds: number | null;
  repetitions: number;
}

export function PriceEstimatePreview({
  outletId, wordCount, spotDurationSeconds, repetitions,
}: PriceEstimatePreviewProps) {
  const enabled = !!outletId;
  const { data, isLoading } = useQuery({
    queryKey: ["media", "outlet-estimate", outletId, wordCount, spotDurationSeconds, repetitions],
    queryFn: () => media.estimateOutletPrice(outletId!, {
      word_count: wordCount ?? undefined,
      spot_duration_seconds: spotDurationSeconds ?? undefined,
      repetitions,
    }),
    enabled,
    staleTime: 30_000,
  });

  if (!enabled) return null;
  if (isLoading) {
    return (
      <div className="text-xs text-ink-400 flex items-center gap-2">
        <Calculator size={12} /> Calculando…
      </div>
    );
  }
  if (!data) return null;

  if (data.cost_amount == null) {
    return (
      <div className="text-xs text-ink-400 flex items-center gap-2 p-2 bg-ink-50 dark:bg-ink-800/50 rounded">
        <Calculator size={12} />
        Este medio no tiene tarifario configurado. Introduce el costo manualmente.
      </div>
    );
  }

  return (
    <div className="text-xs flex items-center justify-between gap-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded border border-emerald-200 dark:border-emerald-800">
      <span className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
        <Calculator size={12} />
        Costo estimado
      </span>
      <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-300">
        {data.currency} {Number(data.cost_amount).toLocaleString("es-PE", { minimumFractionDigits: 2 })}
      </span>
    </div>
  );
}
